import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Painel inicial da Central de Permissões: localizar usuário ou empresa sem
 * navegar por várias telas e abrir a seção correspondente já selecionada.
 */
export default function PermissionsHome() {
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");

  const search = async () => {
    const q = term.trim();
    if (!q) return;
    setSearching(true);
    setError("");
    try {
      const needle = q.toLowerCase();
      const [users, clients] = await Promise.all([
        // GET /users já respeita o escopo do solicitante; o termo é
        // filtrado localmente (mesmo contrato usado pela Gestão de Usuários).
        api.get("/users").catch(() => ({ data: [] })),
        api
          .get("/admin-center/tenants", { params: { search: q, limit: 10 } })
          .catch(() => ({ data: null })),
      ]);
      const rawUsers = Array.isArray(users.data) ? users.data : users.data?.items || [];
      const matched = rawUsers
        .filter(
          (u) =>
            (u.name || "").toLowerCase().includes(needle) ||
            (u.email || "").toLowerCase().includes(needle)
        )
        .slice(0, 10);
      const rawClients =
        clients.data?.items || clients.data?.tenants || clients.data || [];
      setResults({
        users: matched,
        clients: Array.isArray(rawClients) ? rawClients.slice(0, 10) : [],
      });
    } catch (err) {
      setError(err?.response?.data?.detail || "Não foi possível pesquisar agora.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <section className="space-y-4" data-testid="permissions-home">
      <div className="rounded-xl border border-border bg-card p-4">
        <label htmlFor="permissions-search" className="text-sm font-medium">
          Localizar usuário ou empresa
        </label>
        <div className="mt-2 flex gap-2">
          <Input
            id="permissions-search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") search();
            }}
            placeholder="Nome, e-mail, código ou CNPJ"
            className="h-9"
            data-testid="permissions-search-input"
          />
          <Button
            onClick={search}
            disabled={searching}
            className="h-9"
            data-testid="permissions-search-button"
          >
            <Search size={15} className="mr-1.5" aria-hidden="true" />
            {searching ? "Buscando…" : "Buscar"}
          </Button>
        </div>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </div>

      {results && (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-xl border border-border bg-card p-4">
            <h2 className="text-sm font-semibold">Usuários</h2>
            {results.users.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">Nenhum usuário encontrado.</p>
            ) : (
              <ul className="mt-2 divide-y divide-border">
                {results.users.map((user) => (
                  <li key={user.id} className="flex items-center justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{user.name}</p>
                      <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/admin/permissoes/usuarios/${user.id}`)}
                      data-testid={`permissions-open-user-${user.id}`}
                    >
                      Gerenciar permissões
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <h2 className="text-sm font-semibold">Empresas</h2>
            {results.clients.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">Nenhuma empresa encontrada.</p>
            ) : (
              <ul className="mt-2 divide-y divide-border">
                {results.clients.map((client) => (
                  <li key={client.id} className="flex items-center justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{client.name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {client.code || client.cnpj || ""}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/admin/permissoes/clientes/${client.id}`)}
                      data-testid={`permissions-open-client-${client.id}`}
                    >
                      Módulos contratados
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
